import { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  Card,
  Fade,
  Grow,
  alpha,
  useTheme,
  useMediaQuery,
  Chip,
  IconButton,
  Tooltip,
  Snackbar,
  Avatar,
} from "@mui/material";
import { addDoc, collection, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import SignatureCanvas from "react-signature-canvas";
import {
  Clear,
  ArrowBack,
  CheckCircle,
  Event,
  Schedule,
  LocationOn,
  Person,
  Work,
  Phone,
  Edit,
  QrCode,
  Verified,
  Today,
} from "@mui/icons-material";

export default function RegisterAttendance() {
  const { meetingId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [meeting, setMeeting] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    position: "",
    notelp: "",
  });
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const sigCanvas = useRef({});

  useEffect(() => {
    const loadMeeting = async () => {
      try {
        const docRef = doc(db, "meetings", meetingId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setMeeting({ id: docSnap.id, ...docSnap.data() });
        } else {
          setError("Meeting tidak ditemukan");
          setSnackbarMessage("Meeting tidak ditemukan");
          setSnackbarOpen(true);
        }
      } catch (err) {
        setError("Gagal memuat detail meeting");
        setSnackbarMessage("Gagal memuat detail meeting");
        setSnackbarOpen(true);
        console.error(err);
      }
    };

    loadMeeting();
  }, [meetingId]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleClear = () => {
    sigCanvas.current.clear();
  };

  const getTrimmedCanvas = () => {
    const canvas = sigCanvas.current.getCanvas();
    const ctx = canvas.getContext("2d");
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

    // Find boundaries of non-transparent pixels
    let top = canvas.height,
      bottom = 0,
      left = canvas.width,
      right = 0;

    for (let y = 0; y < canvas.height; y++) {
      for (let x = 0; x < canvas.width; x++) {
        const alpha = imageData.data[(y * canvas.width + x) * 4 + 3];
        if (alpha > 0) {
          top = Math.min(top, y);
          bottom = Math.max(bottom, y);
          left = Math.min(left, x);
          right = Math.max(right, x);
        }
      }
    }

    // Add some padding
    const padding = 10;
    top = Math.max(0, top - padding);
    bottom = Math.min(canvas.height, bottom + padding);
    left = Math.max(0, left - padding);
    right = Math.min(canvas.width, right + padding);

    // Create new canvas with trimmed dimensions
    const trimmedCanvas = document.createElement("canvas");
    const trimmedCtx = trimmedCanvas.getContext("2d");

    const width = right - left;
    const height = bottom - top;

    trimmedCanvas.width = width;
    trimmedCanvas.height = height;

    trimmedCtx.putImageData(ctx.getImageData(left, top, width, height), 0, 0);

    return trimmedCanvas;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      setError("Mohon berikan tanda tangan Anda");
      setSnackbarMessage("Mohon berikan tanda tangan Anda");
      setSnackbarOpen(true);
      setLoading(false);
      return;
    }

    try {
      let signatureData;

      // Try built-in method first, fallback to manual trimming
      try {
        signatureData = sigCanvas.current
          .getTrimmedCanvas()
          .toDataURL("image/png");
      } catch (err) {
        console.warn("Using fallback trimming method:", err);
        signatureData = getTrimmedCanvas().toDataURL("image/png");
      }

      await addDoc(collection(db, `meetings/${meetingId}/attendances`), {
        ...formData,
        signature: signatureData,
        attendedAt: new Date().toISOString(),
      });

      setSuccess(true);
      setSnackbarMessage("Kehadiran berhasil direkam!");
      setSnackbarOpen(true);
    } catch (err) {
      setError(err.message || "Gagal mendaftarkan kehadiran");
      setSnackbarMessage(err.message || "Gagal mendaftarkan kehadiran");
      setSnackbarOpen(true);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (!meeting) {
    return (
      <Fade in>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="80vh"
          sx={{ p: 3 }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 4,
              textAlign: "center",
              maxWidth: 400,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
            }}
          >
            {error ? (
              <>
                <Alert severity="error" sx={{ borderRadius: 3 }}>
                  {error}
                </Alert>
                <Button
                  variant="outlined"
                  onClick={() => window.location.reload()}
                  sx={{ mt: 2, borderRadius: 3 }}
                >
                  Coba Lagi
                </Button>
              </>
            ) : (
              <>
                <CircularProgress size={60} thickness={4} />
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  Memuat data meeting...
                </Typography>
              </>
            )}
          </Paper>
        </Box>
      </Fade>
    );
  }

  if (success) {
    return (
      <Fade in timeout={500}>
        <Box sx={{ maxWidth: 600, mx: "auto", p: { xs: 1, sm: 2, md: 3 } }}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            }}
          >
            {/* Success Header */}
            <Box
              sx={{
                p: { xs: 3, sm: 4 },
                background: `linear-gradient(135deg, #1b5e20 0%, #2e7d32 50%, #388e3c 100%)`,
                color: "white",
                textAlign: "center",
              }}
            >
              <Grow in timeout={500}>
                <Box>
                  <CheckCircle sx={{ fontSize: 64, mb: 2 }} />
                  <Typography variant="h4" fontWeight={700} gutterBottom>
                    Kehadiran Tercatat!
                  </Typography>
                  <Typography variant="h6" fontWeight={400}>
                    Terima kasih telah mendaftar
                  </Typography>
                </Box>
              </Grow>
            </Box>

            {/* Success Content */}
            <Box sx={{ p: { xs: 3, sm: 4 } }}>
              <Stack spacing={3}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color="text.secondary"
                >
                  Detail Rapat
                </Typography>

                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                  }}
                >
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    {meeting.name}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Stack spacing={1.5}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Today sx={{ color: "text.secondary", fontSize: 20 }} />
                      <Typography variant="body1">
                        {meeting.day}, {meeting.date}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Schedule
                        sx={{ color: "text.secondary", fontSize: 20 }}
                      />
                      <Typography variant="body1">
                        {meeting.startTime} - {meeting.endTime}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <LocationOn
                        sx={{ color: "text.secondary", fontSize: 20 }}
                      />
                      <Typography variant="body1">
                        {meeting.location}
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>

                {/* Participant Info */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    bgcolor: alpha(theme.palette.success.main, 0.04),
                    border: `1px solid ${alpha(theme.palette.success.main, 0.08)}`,
                  }}
                >
                  <Stack spacing={1.5}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Verified sx={{ color: "#2e7d32", fontSize: 20 }} />
                      <Typography variant="body1" fontWeight={500}>
                        {formData.name}
                      </Typography>
                      <Chip
                        label={formData.position}
                        size="small"
                        sx={{
                          bgcolor: alpha("#2e7d32", 0.1),
                          color: "#2e7d32",
                          fontWeight: 500,
                        }}
                      />
                    </Stack>
                    {formData.notelp && (
                      <Typography variant="body2" color="text.secondary">
                        📱 {formData.notelp}
                      </Typography>
                    )}
                  </Stack>
                </Paper>

                <Button
                  variant="contained"
                  startIcon={<ArrowBack />}
                  onClick={() => window.location.reload()}
                  sx={{
                    borderRadius: 3,
                    py: 1.5,
                    bgcolor: "#2e7d32",
                    "&:hover": { bgcolor: "#1b5e20" },
                  }}
                >
                  Daftar Ulang
                </Button>
              </Stack>
            </Box>
          </Card>
        </Box>
      </Fade>
    );
  }

  return (
    <Fade in timeout={500}>
      <Box sx={{ maxWidth: 600, mx: "auto", p: { xs: 1, sm: 2, md: 3 } }}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
            boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.06)}`,
          }}
        >
          {/* Header - Gradient Design */}
          <Box
            sx={{
              p: { xs: 3, sm: 4 },
              background: `linear-gradient(135deg, #1b5e20 0%, #2e7d32 40%, #388e3c 100%)`,
              color: "white",
              textAlign: "center",
            }}
          >
            <Typography
              variant={isMobile ? "h5" : "h4"}
              fontWeight={800}
              sx={{ letterSpacing: -0.5 }}
            >
              Form Daftar Kehadiran
            </Typography>
            <Typography
              variant={isMobile ? "subtitle1" : "h6"}
              fontWeight={500}
              sx={{ mt: 1, opacity: 0.9 }}
            >
              {meeting.name}
            </Typography>
            <Chip
              icon={<QrCode sx={{ fontSize: 16 }} />}
              label="Scan QR Code untuk daftar"
              size="small"
              sx={{
                mt: 2,
                bgcolor: alpha(theme.palette.common.white, 0.15),
                color: "white",
                fontWeight: 600,
                borderRadius: 2,
              }}
            />
          </Box>

          {/* Form Content */}
          <Box sx={{ p: { xs: 3, sm: 4 } }}>
            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: 3,
                  "& .MuiAlert-icon": { fontSize: 24 },
                }}
                action={
                  <IconButton
                    size="small"
                    onClick={() => setError("")}
                    sx={{ color: "error.main" }}
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                }
              >
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                {/* Meeting Info - Compact */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                  }}
                >
                  <Stack spacing={1.5}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Today sx={{ color: "#2e7d32", fontSize: 18 }} />
                      <Typography variant="body2" fontWeight={500}>
                        {meeting.day}, {meeting.date}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Schedule sx={{ color: "#2e7d32", fontSize: 18 }} />
                      <Typography variant="body2" fontWeight={500}>
                        {meeting.startTime} - {meeting.endTime}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <LocationOn sx={{ color: "#2e7d32", fontSize: 18 }} />
                      <Typography variant="body2" fontWeight={500}>
                        {meeting.location}
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>

                {/* Form Fields with Icons */}
                <TextField
                  fullWidth
                  label="Nama Lengkap"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  placeholder="Masukkan nama lengkap Anda"
                  InputProps={{
                    startAdornment: (
                      <Person sx={{ color: "text.secondary", mr: 1 }} />
                    ),
                    sx: { borderRadius: 3 },
                  }}
                />

                <TextField
                  fullWidth
                  label="Posisi / Jabatan"
                  name="position"
                  value={formData.position}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  placeholder="Masukkan posisi atau jabatan Anda"
                  InputProps={{
                    startAdornment: (
                      <Work sx={{ color: "text.secondary", mr: 1 }} />
                    ),
                    sx: { borderRadius: 3 },
                  }}
                />

                <TextField
                  fullWidth
                  label="No. Telepon (Opsional)"
                  name="notelp"
                  type="tel"
                  value={formData.notelp}
                  onChange={handleChange}
                  variant="outlined"
                  placeholder="08xxxxxxxxxx"
                  InputProps={{
                    startAdornment: (
                      <Phone sx={{ color: "text.secondary", mr: 1 }} />
                    ),
                    sx: { borderRadius: 3 },
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                />

                {/* Signature Section - Enhanced */}
                <Box>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 2 }}
                  >
                    <Typography variant="subtitle1" fontWeight={600}>
                      Tanda Tangan
                    </Typography>
                    <Chip
                      label="Wajib"
                      size="small"
                      color="error"
                      sx={{ fontWeight: 600, borderRadius: 2 }}
                    />
                  </Stack>

                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      borderColor: alpha(theme.palette.primary.main, 0.15),
                      bgcolor: alpha(theme.palette.background.default, 0.3),
                      transition: "all 0.3s ease",
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.08)}`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        height: 0,
                        paddingBottom: "45%",
                      }}
                    >
                      <SignatureCanvas
                        ref={sigCanvas}
                        canvasProps={{
                          className: "signature-canvas",
                          style: {
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            border: `2px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
                            borderRadius: "8px",
                            backgroundColor: "#ffffff",
                            cursor: "crosshair",
                          },
                        }}
                        penColor="#1b5e20"
                        backgroundColor="#ffffff"
                        minWidth={2}
                        maxWidth={3}
                      />
                    </Box>
                  </Paper>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mt: 1.5 }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Tanda tangan di atas
                    </Typography>
                    <Button
                      onClick={handleClear}
                      startIcon={<Clear />}
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{
                        borderRadius: 3,
                        textTransform: "none",
                        "&:hover": {
                          bgcolor: alpha(theme.palette.error.main, 0.08),
                        },
                      }}
                    >
                      Bersihkan
                    </Button>
                  </Stack>
                </Box>

                {/* Submit Button - Enhanced */}
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={loading}
                  sx={{
                    py: 2,
                    borderRadius: 3,
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    bgcolor: "#2e7d32",
                    boxShadow: `0 4px 16px ${alpha("#2e7d32", 0.3)}`,
                    "&:hover": {
                      bgcolor: "#1b5e20",
                      transform: "translateY(-2px)",
                      boxShadow: `0 6px 24px ${alpha("#2e7d32", 0.4)}`,
                    },
                    "&:disabled": {
                      bgcolor: alpha("#2e7d32", 0.6),
                    },
                    transition: "all 0.3s ease",
                    textTransform: "none",
                  }}
                >
                  {loading ? (
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <CircularProgress size={24} sx={{ color: "white" }} />
                      <Typography color="white">Memproses...</Typography>
                    </Stack>
                  ) : (
                    "Submit Daftar Hadir"
                  )}
                </Button>

                {/* Footer Note */}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  align="center"
                  sx={{ mt: 1 }}
                >
                  * Pastikan data yang Anda masukkan sudah benar
                </Typography>
              </Stack>
            </form>
          </Box>
        </Card>

        {/* Snackbar for Notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            severity={error ? "error" : "success"}
            sx={{
              borderRadius: 3,
              boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.12)}`,
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </Fade>
  );
}
